'use client';
import PropTypes from 'prop-types';
import React__default, { forwardRef } from 'react';
import { LABEL_POSITION, TEXTFIELD_SIZE, TEXTFIELD_TYPE, TEXTFIELD_VARIANT } from './TextField.constants.js';
import { Container, TextFieldLabel, InputContainer, StyledInput, ErrorContainer, IconWrapper } from './TextField.styles.js';
import { shouldPreventAdornment } from './TextField.utils.js';
import ButtonIcon from '../Button/ButtonIcon/ButtonIcon.js';
import Icon from '../../Icon/index.js';
import Typography from '../../Typography/index.js';

var defaultProps = {
  adornmentDisabled: false,
  adornmentIcon: undefined,
  adornmentOnClick: undefined,
  adornmentTitle: undefined,
  autoComplete: true,
  errorMessage: undefined,
  isDisabled: false,
  isRequired: false,
  label: undefined,
  labelPosition: LABEL_POSITION.TOP,
  maxLength: undefined,
  onKeyDown: undefined,
  onKeyUp: undefined,
  placeholder: '',
  readOnly: false,
  size: TEXTFIELD_SIZE.REGULAR,
  step: undefined,
  testId: undefined,
  type: TEXTFIELD_TYPE.TEXT,
  value: undefined,
  variant: TEXTFIELD_VARIANT.PRIMARY
};
var TextField = forwardRef(function () {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : defaultProps;
  var inputRef = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : undefined;
  var adornmentDisabled = props.adornmentDisabled,
    adornmentIcon = props.adornmentIcon,
    adornmentOnClick = props.adornmentOnClick,
    adornmentTitle = props.adornmentTitle,
    autoComplete = props.autoComplete,
    errorMessage = props.errorMessage,
    id = props.id,
    isDisabled = props.isDisabled,
    isRequired = props.isRequired,
    label = props.label,
    labelPosition = props.labelPosition,
    maxLength = props.maxLength,
    onChange = props.onChange,
    onKeyDown = props.onKeyDown,
    onKeyUp = props.onKeyUp,
    placeholder = props.placeholder,
    size = props.size,
    step = props.step,
    type = props.type,
    value = props.value,
    variant = props.variant,
    testId = props.testId,
    readOnly = props.readOnly;
  var shouldDisplayAdornment = adornmentIcon && adornmentOnClick && !shouldPreventAdornment(type);
  return React__default.createElement(Container, {
    $labelPosition: labelPosition,
    $variant: variant,
    "data-testid": testId
  }, size !== TEXTFIELD_SIZE.TINY && label && React__default.createElement(TextFieldLabel, {
    id: "label-".concat(id),
    htmlFor: "textfield-".concat(id),
    "aria-label": label,
    $labelPosition: labelPosition,
    $variant: variant
  }, label), React__default.createElement(InputContainer, {
    $variant: variant
  }, React__default.createElement(StyledInput, {
    id: "textfield-".concat(id),
    autoComplete: autoComplete ? 'on' : 'off',
    disabled: isDisabled,
    $errorMessage: errorMessage,
    onChange: onChange,
    placeholder: placeholder,
    ref: inputRef,
    required: isRequired,
    $shouldDisplayCustomAdornment: shouldDisplayAdornment,
    $size: size,
    step: step,
    type: type,
    value: value,
    $variant: variant,
    maxLength: maxLength,
    onKeyDown: onKeyDown,
    onKeyUp: onKeyUp,
    readOnly: readOnly
  }), shouldDisplayAdornment && React__default.createElement(ButtonIcon, {
    disabled: adornmentDisabled,
    icon: adornmentIcon,
    onClick: adornmentOnClick,
    size: size,
    title: adornmentTitle,
    variant: "ghost"
  })), errorMessage && React__default.createElement(ErrorContainer, null, React__default.createElement(IconWrapper, null, React__default.createElement(Icon, {
    variant: "CloseSolid",
    size: "small"
  })), React__default.createElement(Typography, {
    variant: "bodyRegular"
  }, errorMessage)));
});
TextField.defaultProps = defaultProps;
TextField.propTypes = {
  adornmentDisabled: PropTypes.bool,
  adornmentIcon: PropTypes.string,
  adornmentOnClick: PropTypes.func,
  adornmentTitle: PropTypes.string,
  autoComplete: PropTypes.bool,
  errorMessage: PropTypes.string,
  id: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
  isRequired: PropTypes.bool,
  label: PropTypes.string,
  labelPosition: PropTypes.oneOf(Object.values(LABEL_POSITION)),
  maxLength: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  size: PropTypes.oneOf(Object.values(TEXTFIELD_SIZE)),
  step: PropTypes.number,
  testId: PropTypes.string,
  type: PropTypes.oneOf(Object.values(TEXTFIELD_TYPE)),
  value: PropTypes.string,
  variant: PropTypes.oneOf(Object.values(TEXTFIELD_VARIANT))
};

export { TextField };
//# sourceMappingURL=TextField.js.map
